/**
 * Trainova AI Client Media Storage (IndexedDB)
 *
 * Persists session video/audio recordings and per-turn learner audio blobs
 * reliably in the browser. Unlike sessionStorage (which loses blob URLs on
 * navigation or refresh), IndexedDB stores actual binary Blobs that survive
 * page refreshes, tab reloads, and network latency.
 */

const DB_NAME = 'TrainovaMediaDB';
const DB_VERSION = 1;
const RECORDING_STORE = 'session_recordings';
const TURN_AUDIO_STORE = 'turn_audio';

function openDB(): Promise<IDBDatabase> {
  return new Promise((resolve, reject) => {
    if (typeof window === 'undefined' || typeof indexedDB === 'undefined') {
      return reject(new Error('IndexedDB not supported in this environment'));
    }
    const req = indexedDB.open(DB_NAME, DB_VERSION);
    req.onupgradeneeded = () => {
      const db = req.result;
      if (!db.objectStoreNames.contains(RECORDING_STORE)) {
        db.createObjectStore(RECORDING_STORE);
      }
      if (!db.objectStoreNames.contains(TURN_AUDIO_STORE)) {
        db.createObjectStore(TURN_AUDIO_STORE);
      }
    };
    req.onsuccess = () => resolve(req.result);
    req.onerror = () => reject(req.error || new Error('Failed to open IndexedDB'));
  });
}

/**
 * Stores full session meeting recording blob (WebM/MP4) for an attempt.
 */
export async function saveSessionRecordingBlob(attemptId: string, blob: Blob): Promise<void> {
  if (!attemptId || !blob || blob.size === 0) return;
  try {
    const db = await openDB();
    return new Promise((resolve, reject) => {
      const tx = db.transaction(RECORDING_STORE, 'readwrite');
      const store = tx.objectStore(RECORDING_STORE);
      const req = store.put(blob, `attempt_${attemptId}_video`);
      req.onsuccess = () => resolve();
      req.onerror = () => reject(req.error);
    });
  } catch (err) {
    console.warn('[Trainova MediaStorage] saveSessionRecordingBlob note:', err);
  }
}

/**
 * Retrieves the full session meeting recording blob for an attempt.
 */
export async function getSessionRecordingBlob(attemptId: string): Promise<Blob | null> {
  if (!attemptId) return null;
  try {
    const db = await openDB();
    return new Promise((resolve, reject) => {
      const tx = db.transaction(RECORDING_STORE, 'readonly');
      const store = tx.objectStore(RECORDING_STORE);
      const req = store.get(`attempt_${attemptId}_video`);
      req.onsuccess = () => resolve(req.result || null);
      req.onerror = () => reject(req.error);
    });
  } catch {
    return null;
  }
}

/**
 * Stores an individual learner turn audio blob for an attempt.
 */
export async function saveTurnAudioBlob(attemptId: string, turnIndex: number | string, blob: Blob): Promise<void> {
  if (!attemptId || !blob || blob.size === 0) return;
  try {
    const db = await openDB();
    return new Promise((resolve, reject) => {
      const tx = db.transaction(TURN_AUDIO_STORE, 'readwrite');
      const store = tx.objectStore(TURN_AUDIO_STORE);
      const req = store.put(blob, `attempt_${attemptId}_turn_${turnIndex}`);
      req.onsuccess = () => resolve();
      req.onerror = () => reject(req.error);
    });
  } catch (err) {
    console.warn('[Trainova MediaStorage] saveTurnAudioBlob note:', err);
  }
}

/**
 * Retrieves an individual learner turn audio blob for an attempt.
 */
export async function getTurnAudioBlob(attemptId: string, turnIndex: number | string): Promise<Blob | null> {
  if (!attemptId) return null;
  try {
    const db = await openDB();
    return new Promise((resolve, reject) => {
      const tx = db.transaction(TURN_AUDIO_STORE, 'readonly');
      const store = tx.objectStore(TURN_AUDIO_STORE);
      const req = store.get(`attempt_${attemptId}_turn_${turnIndex}`);
      req.onsuccess = () => resolve(req.result || null);
      req.onerror = () => reject(req.error);
    });
  } catch {
    return null;
  }
}

